import {
  Component, Input, Output, EventEmitter, OnInit, OnDestroy,
  ChangeDetectorRef, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CustomsCalculationsService, CustomsCalculation,
  UpdateCustomsProductBrokerRequest, RefineCalculationRequest, CertificateChecklist,
  UpdateShipmentInfoRequest, CertificatesProcessingStatus
} from '../../../../services/api/customs-calculations';
import { CustomsProduct, FollowUpQuestion } from '../../../../services/api/customs-calculation';
import { LanguageService } from '../../../../services/i18n/language';
import { AuthService } from '../../../../services/auth/auth';
import { CalculationSummaryPanelComponent } from '../calculation-summary-panel/calculation-summary-panel';
import { InvoiceEditorComponent } from '../invoice-editor/invoice-editor';
import { CertificatesSocketService } from '../../../../services/api/certificates-socket';
import { CurrencyRatesService } from '../../../../services/currency-rates';
import { RouterModule } from '@angular/router';
import { CALC_SECTIONS, CalcSection } from '../calculation-sections';
import { EMPTY, Subscription, interval, merge, timer } from 'rxjs';
import { catchError, startWith, switchMap, take } from 'rxjs/operators';
import { ConfirmDialogService } from '../../../../services/confirm-dialog';

/** Состояние шага дорожной карты: готово / нужно действие пользователя / ждём систему / справка. */
type ProgressState = 'done' | 'action' | 'wait' | 'info';
interface StepStatus { state: ProgressState; key: string; fallback: string; }

@Component({
  selector: 'app-view-calculation-details',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule, RouterModule, CalculationSummaryPanelComponent, InvoiceEditorComponent],
  templateUrl: './view-calculation-details.html',
  styleUrl: './view-calculation-details.scss'
})
export class ViewCalculationDetailsComponent implements OnInit, OnDestroy, OnChanges {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);

  @Input() calculation: CustomsCalculation | null = null;
  @Input() loading = false;
  @Input() error: string | null = null;
  @Input() showBackButton = true;
  /** Раздел кабинета клиента (боковое меню): показываем только его; null — вся страница, как у брокера и поставщика. */
  @Input() section: CalcSection | null = null;
  @Output() back = new EventEmitter<void>();

  // Translations
  titleText = 'Просмотр расчета';
  backText = 'Назад к списку';
  calculationTitleText = 'Название расчета';
  productsText = 'Товары';
  aiIdentifiedAsText = 'ИИ определил как';
  productNameText = 'Наименование товара';
  productCostText = 'Стоимость товара';
  tnvedCodeText = 'Код ТНВЭД';
  customsFeesText = 'Таможенный сбор';
  customsDutyText = 'Таможенная пошлина';
  vatText = 'НДС';
  totalPaymentsText = 'Общие платежи';
  totalAllPaymentsText = 'Итого';
  noProductsText = 'Товары не добавлены';
  completedWithAiText = 'Выполнено с помощью AI';
  createdAtText = 'Дата создания';
  loadingText = 'Загрузка...';
  codeNotFoundText = 'Код не найден';
  detailedAssessmentText = 'Расчёт носит предварительный информационный характер. Для детальной оценки и официального расчёта таможенных платежей обращайтесь в администрацию сайта. Услуга предоставляется на платной основе.';
  whatsappContactText = 'Свяжитесь с нами по WhatsApp';
  readonly whatsappUrl = 'https://wa.me/77078880054';

  /** WhatsApp из блока «Возьмём поставку под контроль»: сообщение уже с номером расчёта. */
  get ctaWhatsappLink(): string {
    const text = this.st('calcSections.ctaWhatsappMessage', 'Здравствуйте! Вопрос по расчёту № {id} на Bilload')
      .replace('{id}', String(this.calculation?.id ?? ''));
    return `${this.whatsappUrl}?text=${encodeURIComponent(text)}`;
  }

  calculationFallbackPrefixText = 'Расчет #';
  brokerCommentSectionTitleText = 'Комментарий брокера';
  brokerCommentPlaceholderText = 'Оставьте комментарий по расчету';
  savingEllipsisText = 'Сохранение...';
  saveBrokerCommentText = 'Сохранить комментарий';
  preliminaryNoticeTitleText = 'Расчёт является предварительным';
  preliminaryNoticeBodyText = 'Для некоторых товаров не удалось учесть специфические ставки пошлин, акциз или антидемпинговые пошлины. Нажмите кнопку «Уточнить (шаг 2)» рядом с нужным товаром, чтобы получить точный результат.';
  actionsText = 'Действия';
  brokerActionsText = 'Действия брокера';
  codeInDictionaryTitleText = 'Код есть в справочнике';
  exciseNotCalculatedTitleText = 'Акциз не рассчитывается: алкоголь/табак не обслуживаются сервисом';
  exciseNotCalculatedBadgeText = '⚠ Акциз не считается';
  refineButtonTitleText = 'Уточнить расчёт (шаг 2)';
  refineCloseLabelText = '▲ Закрыть';
  refineDoneLabelText = '✓ Уточнено';
  refineOpenLabelText = '⚙ Уточнить';
  certificatesNotCheckedText = 'Не проверено';
  certificatesCheckNotDoneText = 'Расчёт документов для этого кода ещё не выполнялся.';
  certificatesRunCheckText = 'Сделать расчёт документов';
  certificatesCheckingText = 'Проверка…';
  certificatesTitleAttrText = 'Разрешительные документы';
  certificatesCloseLabelText = '▲ Документы';
  certificatesOpenLabelText = '📄 Документы';
  saveText = 'Сохранить';
  deleteText = 'Удалить';
  refineSectionTitleText = '⚙ Уточнённый расчёт — шаг 2';
  refineSectionDescriptionText = 'Укажите дополнительные данные, чтобы получить точный расчёт с учётом страны происхождения, специфических ставок и акциза. Результат сохраняется и учитывается в сводном итоге расчёта.';
  refineNoQuestionsText = 'Для этого товара дополнительных вопросов нет.';
  enterPrefixText = 'Введите';
  defaultUnitText = 'количество';
  refinePreviewButtonTitleText = 'Посчитать и посмотреть результат, не сохраняя — можно менять ответы и пробовать снова';
  calculatingText = 'Расчёт...';
  calculateButtonText = '🔍 Рассчитать';
  refineSaveButtonTitleText = 'Сохранить ответы и результат — учитывается в сводном итоге расчёта';
  saveButtonWithIconText = '💾 Сохранить';
  cancelText = 'Отмена';
  savedText = 'Сохранено';
  previewResultUnsavedText = 'Предварительный результат (не сохранён)';
  countryOfOriginText = 'Страна происхождения';
  dutyRateText = 'Ставка пошлины';
  exciseText = 'Акциз';
  antiDumpingDutyText = 'Антидемпинговая пошлина';
  totalCustomsPaymentsFullText = 'Итого таможенные платежи';
  tnvedColonLabelText = 'ТНВЭД:';
  cachedResultSuffixText = '(сохранённый результат)';
  noNonTariffDataText = 'ℹ Нетарифные данные для данного кода ТНВЭД отсутствуют в справочнике. Рекомендуем обратиться к таможенному брокеру для уточнения.';
  requiredDocumentsTitleText = 'Требуемые разрешительные документы';
  noDocumentsRequiredText = '✓ По имеющимся данным дополнительные разрешительные документы не требуются';
  noteLabelText = 'Примечание:';
  aiDisclaimerText = 'ℹ Данные получены с использованием AI на основе нормативных актов ЕАЭС. Для официального оформления обращайтесь к аккредитованному таможенному брокеру.';
  reasonSpecificRateText = 'специфическая ставка (EUR/KZT за ед.)';
  reasonMinCombinedRateText = 'минимальная часть комбинированной ставки';
  reasonExciseLowerText = 'акциз';
  reasonAntiDumpingLowerText = 'антидемпинговая пошлина';
  refineHintFallbackText = 'Уточните данные в шаге 2';
  notAccountedPrefixText = 'Не учтено: ';

  // Broker state
  brokerCommentDraft = '';
  brokerSavingComment = false;
  brokerError: string | null = null;
  brokerSuccess: string | null = null;
  editableProducts: Record<number, UpdateCustomsProductBrokerRequest> = {};
  savingProducts: Record<number, boolean> = {};
  deletingProducts: Record<number, boolean> = {};

  // ─── Шаг 2 (уточнённый расчёт) ───────────────────────────────────────────

  /** ID товара, для которого открыта панель уточнения */
  refineProductId: number | null = null;
  /** Ответы пользователя на follow-up вопросы */
  refineAnswers: Record<string, string | number | boolean> = {};
  /** Загрузка предварительного расчёта (без сохранения) */
  refinePreviewLoading = false;
  /** Загрузка сохранения */
  refineLoading = false;
  refineError: string | null = null;
  /** Результат расчёта — предварительный или сохранённый, см. refineResultSaved */
  refineResult: CustomsProduct | null = null;
  /** true — refineResult уже записан в БД; false — это просто предпросмотр */
  refineResultSaved = false;

  // ─── Разрешительные документы (по кодам ТНВЭД) ────────────────────────────

  certificatesChecking = false;
  /** ID товара, для которого сейчас раскрыта инлайн-панель документов (без запроса к бэку — данные уже загружены). */
  certProductId: number | null = null;

  // ─── Сводная панель (информация о поставке + агрегаты) ────────────────────

  detailsOpen = false;
  shipmentSaving = false;
  estimatingDelivery = false;
  deliveryEstimateError: string | null = null;
  /** Отдельный флаг от shipmentSaving — иначе смена Incoterm в блоке инвойса выглядела
   *  бы для сводной панели как её собственное сохранение (см. onInvoiceIncotermChange). */
  incotermSaving = false;

  private subscriptions = new Subscription();

  /** Курсы к тенге — чтобы в «Итого» сложить доставку (она в валюте) с платежами в тенге. */
  private rates: Record<string, number> = {};

  constructor(
    private languageService: LanguageService,
    private customsCalculationsService: CustomsCalculationsService,
    private authService: AuthService,
    private certificatesSocket: CertificatesSocketService,
    private currencyRates: CurrencyRatesService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe(lang => {
        this.numberLocale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.add(
      this.currencyRates.getRates().subscribe(r => {
        this.rates = { ...r };
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['calculation']) {
      this.syncEditableState();
      this.closeRefine();
      this.certProductId = null;
    }
    if (changes['calculation'] || changes['section']) {
      this.autoOpenPendingRefine();
    }
  }

  /** Название позиции ТН ВЭД без «– – –» в начале; пустые «прочие» не показываем — они ничего не говорят. */
  cleanTnvedName(name: string | null | undefined): string {
    const text = (name ?? '').replace(/^[\s\-–—•·]+/, '').trim();
    return /^проч(ие|ая|ий|ее)\.?$/i.test(text) ? '' : text;
  }

  /**
   * Вид ставки пошлины. Отдельного поля сервер не присылает — судим по признакам товара:
   * есть часть «за единицу» (вопрос о количестве, указанное количество или минимум за единицу) — специфическая;
   * вместе с процентом — комбинированная; только процент — адвалорная.
   */
  dutyRateType(product: CustomsProduct): 'advalorem' | 'specific' | 'combined' | null {
    const perUnit = !!product.specificMinPresent || !!product.specificRateSkipped
      || Object.keys(product.quantities ?? {}).length > 0
      || (product.followUpQuestions ?? []).some(q => q.id.startsWith('quantity_'));
    const percent = Number(product.customsDutyPercent ?? 0);
    if (perUnit) return percent > 0 ? 'combined' : 'specific';
    return product.customsDutyPercent != null ? 'advalorem' : null;
  }

  /** Подпись ставки: адвалорная — процент, специфическая — «СП», комбинированная — «КБ». */
  dutyRateLabel(product: CustomsProduct): string {
    switch (this.dutyRateType(product)) {
      case 'advalorem':
        return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 2 }).format(Number(product.customsDutyPercent)) + '%';
      case 'specific':
        return this.st('calcSections.dutySpecificShort', 'СП');
      case 'combined':
        return this.st('calcSections.dutyCombinedShort', 'КБ');
      default:
        return '';
    }
  }

  /** Расшифровка сокращения — во всплывающей подсказке. */
  dutyRateHint(product: CustomsProduct): string {
    switch (this.dutyRateType(product)) {
      case 'advalorem':
        return this.st('calcSections.dutyHintAdvalorem', 'Адвалорная ставка — процент от таможенной стоимости');
      case 'specific':
        return this.st('calcSections.dutyHintSpecific', 'Специфическая ставка — сумма за единицу товара (кг, шт., л)');
      case 'combined':
        return this.st('calcSections.dutyHintCombined', 'Комбинированная ставка — {pct}% от таможенной стоимости и ставка за единицу товара')
          .replace('{pct}', new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 2 }).format(Number(product.customsDutyPercent ?? 0)));
      default:
        return '';
    }
  }

  /** Админ смотрит расчёт клиента — только просмотр: ничего не сохраняется от его имени. */
  get readOnly(): boolean {
    return this.authService.getUser()?.role?.code === 'ROLE_ADMIN';
  }

  /** Ставка НДС Казахстана — на товаре её нет, показываем рядом с суммой НДС. */
  readonly vatPercent = 16;

  /** Товары, по которым ИИ задал вопросы, а клиент ещё не ответил. */
  get pendingRefineProducts(): CustomsProduct[] {
    return (this.calculation?.products ?? []).filter(p => this.hasFollowUpQuestions(p) && !p.refined);
  }

  /** Товар, вопросы которого сейчас открыты в блоке наверху (не уточнённый). */
  get topRefineProduct(): CustomsProduct | null {
    const p = this.getRefineProduct();
    return p && !p.refined ? p : null;
  }

  /** Кабинет, «Таможенные платежи»: сразу открываем вопросы по первому неуточнённому товару. */
  private autoOpenPendingRefine(): void {
    if (this.section !== 'payments' || this.refineProductId !== null) return;
    const next = this.pendingRefineProducts[0];
    if (next) {
      this.openRefine(next);
    }
  }

  cancelInlineRefine(): void {
    this.closeRefine();
    this.autoOpenPendingRefine();
  }

  statusLabel(status?: string): string {
    const map: Record<string, [string, string]> = {
      CREATED: ['common.supplies.statusCreated', 'Создан'],
      IN_PROCESSING: ['common.supplies.statusInProcessing', 'В обработке'],
      PROCESSED: ['common.supplies.statusProcessed', 'Обработан']
    };
    const [key, fallback] = map[status || 'CREATED'] ?? map['CREATED'];
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  isBroker(): boolean {
    return this.authService.getUser()?.role?.code === 'ROLE_CUSTOMS_BROKER';
  }

  /** Поставщику не нужны логистика (страны/вес/тип перевозки) и итоги таможенных
   *  платежей — ему важен только список товаров (см. app-invoice-editor выше), чтобы
   *  свериться с заказом. */
  /** Новый пользователь по номеру телефона: полный доступ к платформе откроется после заявки на сопровождение. */
  isPublicUser(): boolean {
    return this.authService.getUser()?.role?.code === 'ROLE_PUBLIC';
  }

  readonly fullAccessFeatures = [
    { key: 'calcSections.featureForwarders', fallback: 'Заявки экспедиторам и сравнение ставок' },
    { key: 'calcSections.featureSupplier', fallback: 'Обмен документами с поставщиком' },
    { key: 'calcSections.featureDeals', fallback: 'Сделки и отчёты по поставке' },
    { key: 'calcSections.featureChat', fallback: 'Чат с участниками поставки' }
  ];

  isSupplier(): boolean {
    return this.authService.getUser()?.role?.code === 'ROLE_SUPPLIER';
  }

  // ─── Сводная панель ────────────────────────────────────────────────────────

  onToggleDetails(): void {
    this.detailsOpen = !this.detailsOpen;
  }

  /** Сохранить данные о поставке и получить пересчитанные агрегаты по всем товарам */
  onShipmentInfoChange(request: UpdateShipmentInfoRequest): void {
    if (this.readOnly) return;
    if (!this.calculation || this.isBroker()) return;
    this.shipmentSaving = true;
    this.customsCalculationsService.updateShipmentInfo(this.calculation.id, request).subscribe({
      next: (calculation) => {
        this.calculation = calculation;
        this.syncEditableState();
        this.shipmentSaving = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.shipmentSaving = false;
        console.error('Error saving shipment info:', err);
        this.cdr.markForCheck();
      }
    });
  }

  getEditableProduct(product: CustomsProduct): UpdateCustomsProductBrokerRequest {
    if (!this.editableProducts[product.id]) {
      this.editableProducts[product.id] = this.createEditableProduct(product);
    }
    return this.editableProducts[product.id];
  }

  // ─── Broker actions ───────────────────────────────────────────────────────

  saveBrokerComment(): void {
    if (!this.calculation || !this.isBroker()) return;
    this.brokerSavingComment = true;
    this.brokerError = null;
    this.brokerSuccess = null;
    this.customsCalculationsService.updateCalculationByBroker(this.calculation.id, {
      brokerComment: this.brokerCommentDraft
    }).subscribe({
      next: (calculation) => {
        this.calculation = calculation;
        this.syncEditableState();
        this.brokerSavingComment = false;
        this.brokerSuccess = 'Комментарий сохранен';
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.brokerSavingComment = false;
        this.brokerError = err.error?.message || 'Ошибка при сохранении комментария';
        this.cdr.markForCheck();
      }
    });
  }

  saveProduct(product: CustomsProduct): void {
    if (!this.calculation || !this.isBroker()) return;
    const editable = this.getEditableProduct(product);
    this.savingProducts[product.id] = true;
    this.brokerError = null;
    this.brokerSuccess = null;

    this.customsCalculationsService.updateProductByBroker(this.calculation.id, product.id, editable).subscribe({
      next: (updatedProduct) => {
        if (this.calculation?.products) {
          this.calculation.products = this.calculation.products.map(p =>
            p.id === updatedProduct.id ? updatedProduct : p
          );
        }
        this.editableProducts[product.id] = this.createEditableProduct(updatedProduct);
        this.savingProducts[product.id] = false;
        this.brokerSuccess = 'Строка сохранена';
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.savingProducts[product.id] = false;
        this.brokerError = err.error?.message || 'Ошибка при сохранении строки';
        this.cdr.markForCheck();
      }
    });
  }

  async deleteProduct(product: CustomsProduct): Promise<void> {
    if (!this.calculation || !this.isBroker()) return;
    if (!await this.confirmDialog.confirm('Удалить строку расчета?')) return;

    this.deletingProducts[product.id] = true;
    this.brokerError = null;
    this.brokerSuccess = null;

    this.customsCalculationsService.deleteProductByBroker(this.calculation.id, product.id).subscribe({
      next: () => {
        if (this.calculation?.products) {
          this.calculation.products = this.calculation.products.filter(p => p.id !== product.id);
        }
        delete this.editableProducts[product.id];
        this.deletingProducts[product.id] = false;
        this.brokerSuccess = 'Строка удалена';
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.deletingProducts[product.id] = false;
        this.brokerError = err.error?.message || 'Ошибка при удалении строки';
        this.cdr.markForCheck();
      }
    });
  }

  // ─── Шаг 2: уточнённый расчёт ────────────────────────────────────────────

  /** Открыть панель уточнения для товара — если товар уже был уточнён ранее,
   *  подставляет сохранённые ответы, чтобы пользователь мог их увидеть/изменить. */
  openRefine(product: CustomsProduct): void {
    if (this.refineProductId === product.id) {
      // Повторный клик — закрыть
      this.closeRefine();
      return;
    }
    this.refineProductId = product.id;
    this.refineAnswers = this.buildPrefillAnswers(product);
    this.refineResult = null;
    this.refineResultSaved = false;
    this.refineError = null;
    this.cdr.markForCheck();
  }

  /** Восстанавливает ответы на вопросы шага 2 из сохранённых на товаре данных */
  private buildPrefillAnswers(product: CustomsProduct): Record<string, string | number | boolean> {
    const answers: Record<string, string | number | boolean> = {};
    if (!product.refined) return answers;

    if (product.countryOfOrigin) {
      answers['countryOfOrigin'] = product.countryOfOrigin;
    }
    if (product.exciseVariantValue) {
      answers['exciseVariant'] = product.exciseVariantValue;
    }
    if (product.quantities) {
      Object.entries(product.quantities).forEach(([unitKey, qty]) => {
        answers['quantity_' + unitKey] = qty;
      });
    }
    if (product.countryOfOrigin && product.antiDumpingApplicable != null) {
      answers['adMatch_' + product.countryOfOrigin.toUpperCase()] = product.antiDumpingApplicable ? 'true' : 'false';
    }
    if (product.countryOfOrigin && product.antiDumpingProducerValue) {
      answers['adProducer_' + product.countryOfOrigin.toUpperCase()] = product.antiDumpingProducerValue;
    }
    return answers;
  }

  closeRefine(): void {
    this.refineProductId = null;
    this.refineAnswers = {};
    this.refineResult = null;
    this.refineResultSaved = false;
    this.refineError = null;
    this.refineLoading = false;
    this.refinePreviewLoading = false;
  }

  /** Получить товар, для которого открыта панель */
  getRefineProduct(): CustomsProduct | null {
    if (this.refineProductId == null || !this.calculation?.products) return null;
    return this.calculation.products.find(p => p.id === this.refineProductId) ?? null;
  }

  /** Возвращает вопросы для активного товара */
  getRefineQuestions(): FollowUpQuestion[] {
    return this.getRefineProduct()?.followUpQuestions ?? [];
  }

  /**
   * Проверяет, должен ли вопрос отображаться с учётом зависимости (dependsOn*)
   */
  isQuestionVisible(q: FollowUpQuestion): boolean {
    if (!q.dependsOnQuestionId) return true;
    const parentVal = this.refineAnswers[q.dependsOnQuestionId];
    if (parentVal == null) return false;
    return String(parentVal).toUpperCase() === (q.dependsOnValue ?? '').toUpperCase();
  }

  /**
   * Сбросить ответы дочерних вопросов при изменении родительского
   */
  onAnswerChange(questionId: string, questions: FollowUpQuestion[]): void {
    // Сбросить зависимые вопросы
    questions.forEach(q => {
      if (q.dependsOnQuestionId === questionId) {
        delete this.refineAnswers[q.id];
        // Рекурсивно сбросить вложенные
        this.onAnswerChange(q.id, questions);
      }
    });
  }

  /** Можно ли отправить уточнённый расчёт (все обязательные видимые вопросы заполнены) */
  canSubmitRefine(): boolean {
    const questions = this.getRefineQuestions();
    if (questions.length === 0) return false;
    return questions.every(q => {
      if (!this.isQuestionVisible(q) || !q.required) return true;
      const val = this.refineAnswers[q.id];
      if (val == null || val === '') return false;
      if (q.type === 'QUANTITY_INPUT' && Number(val) <= 0) return false;
      return true;
    });
  }

  /** Предварительный расчёт — считает, но НЕ сохраняет. Можно жать сколько угодно раз. */
  previewRefine(): void {
    if (!this.calculation || this.refineProductId == null) return;
    if (!this.canSubmitRefine()) {
      this.refineError = 'Заполните все обязательные поля';
      this.cdr.markForCheck();
      return;
    }

    const request = this.buildRefineRequest();
    this.refinePreviewLoading = true;
    this.refineError = null;
    this.refineResult = null;
    this.refineResultSaved = false;

    this.customsCalculationsService.previewRefineCalculation(
      this.calculation.id, this.refineProductId, request
    ).subscribe({
      next: (result) => {
        this.refineResult = result;
        this.refinePreviewLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.refinePreviewLoading = false;
        this.refineError = err.error?.message || 'Ошибка при расчёте';
        this.cdr.markForCheck();
      }
    });
  }

  /** Сохранить уточнённый расчёт — считает и записывает ответы + результат в БД */
  saveRefine(): void {
    if (this.readOnly) return;
    if (!this.calculation || this.refineProductId == null) return;
    if (!this.canSubmitRefine()) {
      this.refineError = 'Заполните все обязательные поля';
      this.cdr.markForCheck();
      return;
    }

    const request = this.buildRefineRequest();
    this.refineLoading = true;
    this.refineError = null;
    this.refineResult = null;
    this.refineResultSaved = false;

    const calculationId = this.calculation.id;
    this.customsCalculationsService.refineCalculation(
      calculationId, this.refineProductId, request
    ).subscribe({
      next: (result) => {
        this.refineResult = result;
        this.refineResultSaved = true;
        this.refineLoading = false;
        // Результат сохранён на бэке — перезапрашиваем весь расчёт,
        // чтобы сводная панель (агрегаты) наверху тоже пересчиталась.
        this.customsCalculationsService.getCalculationById(calculationId).subscribe({
          next: (calculation) => {
            this.calculation = calculation;
            this.syncEditableState();
            // Кабинет: ответы сохранены — блок уходит, открываются вопросы по следующему товару
            if (this.section === 'payments') {
              this.closeRefine();
              this.autoOpenPendingRefine();
            }
            this.cdr.markForCheck();
          },
          error: () => this.cdr.markForCheck()
        });
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.refineLoading = false;
        this.refineError = err.error?.message || 'Ошибка при сохранении расчёта';
        this.cdr.markForCheck();
      }
    });
  }

  private buildRefineRequest(): RefineCalculationRequest {
    const questions = this.getRefineQuestions();
    const req: RefineCalculationRequest = {};
    const quantities: Record<string, number> = {};

    questions.forEach(q => {
      if (!this.isQuestionVisible(q)) return;
      const val = this.refineAnswers[q.id];
      if (val == null) return;

      switch (q.id) {
        case 'countryOfOrigin':
          req.countryOfOrigin = String(val);
          break;
        case 'exciseVariant':
          req.exciseVariantValue = String(val);
          break;
        default:
          if (q.id.startsWith('quantity_') && q.unitKey) {
            quantities[q.unitKey] = Number(val);
          } else if (q.id.startsWith('adMatch_')) {
            req.antiDumpingApplicable = val === 'true' || val === true;
          } else if (q.id.startsWith('adProducer_')) {
            req.antiDumpingProducerValue = String(val);
          }
      }
    });

    if (Object.keys(quantities).length > 0) {
      req.quantities = quantities;
    }

    return req;
  }

  // ─── Разрешительные документы (по кодам ТНВЭД) ────────────────────────────

  /** Чеклист по коду — undefined значит, что проверка для этого кода ещё не выполнялась. */
  getChecklistForCode(code: string): CertificateChecklist | undefined {
    return this.calculation?.certificateChecklists?.find(c => c.tnvedCode === code);
  }

  getChecklistForProduct(product: CustomsProduct): CertificateChecklist | undefined {
    const code = product.tnvedCodeForBroker || product.tnvedCodeOriginal;
    return code ? this.getChecklistForCode(code) : undefined;
  }

  /** Открыть/закрыть инлайн-панель документов для товара — без запроса к бэку,
   *  данные берутся из уже загруженного calculation.certificateChecklists. */
  toggleCertPanel(product: CustomsProduct): void {
    this.certProductId = this.certProductId === product.id ? null : product.id;
  }

  get certPanelProduct(): CustomsProduct | undefined {
    return this.calculation?.products?.find(p => p.id === this.certProductId);
  }

  runCertificatesCheck(): void {
    if (!this.calculation || this.certificatesChecking) return;
    const calculationId = this.calculation.id;
    this.certificatesChecking = true;
    this.customsCalculationsService.startCertificatesCheck(calculationId).subscribe({
      next: () => this.watchCertificatesProcessing(calculationId),
      error: () => {
        this.certificatesChecking = false;
        this.cdr.markForCheck();
      }
    });
  }

  private watchCertificatesProcessing(calculationId: number): void {
    let finished = false;

    const ws$ = this.certificatesSocket.watch(calculationId).pipe(
      catchError(err => {
        console.warn('Certificates websocket unavailable, using polling', err);
        return EMPTY;
      })
    );

    const poll$ = interval(2000).pipe(
      startWith(0),
      switchMap(() => this.customsCalculationsService.getCertificatesProcessingStatus(calculationId).pipe(
        catchError(() => EMPTY)
      ))
    );

    this.subscriptions.add(
      merge(ws$, poll$).subscribe(status => this.handleCertificatesStatus(calculationId, status, () => finished, v => finished = v))
    );

    this.subscriptions.add(
      timer(5 * 60 * 1000).pipe(take(1)).subscribe(() => {
        if (finished) return;
        finished = true;
        this.certificatesChecking = false;
        this.reloadCalculation(calculationId);
      })
    );
  }

  private handleCertificatesStatus(
    calculationId: number,
    status: CertificatesProcessingStatus,
    isFinished: () => boolean,
    setFinished: (v: boolean) => void
  ): void {
    if (isFinished() || !status || !this.calculation) return;

    this.calculation = {
      ...this.calculation,
      certificatesProcessingStatus: status.status,
      certificatesProcessingMessage: status.message,
      certificatesProcessingCurrent: status.current,
      certificatesProcessingTotal: status.total
    };

    if (status.status === 'DONE' || status.status === 'ERROR') {
      setFinished(true);
      this.certificatesChecking = false;
      this.reloadCalculation(calculationId);
      return;
    }

    this.cdr.markForCheck();
  }

  private reloadCalculation(calculationId: number): void {
    this.customsCalculationsService.getCalculationById(calculationId).subscribe({
      next: (calculation) => {
        this.calculation = calculation;
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck()
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  getTotalAmount(): number {
    if (!this.calculation?.products || this.calculation.products.length === 0) return 0;
    return this.calculation.products.reduce((sum, p) => sum + (p.totalCustomsPayments || 0), 0);
  }

  hasApproximateProducts(): boolean {
    return this.calculation?.products?.some(p => p.approximate) ?? false;
  }

  hasFollowUpQuestions(product: CustomsProduct): boolean {
    return (product.followUpQuestions?.length ?? 0) > 0;
  }

  /** Текстовое описание источника ставки */
  rateSourceLabel(product: CustomsProduct): string {
    switch (product.rateSource) {
      case 'WTO': return 'ВТО';
      case 'ETT': return 'ЕТТ';
      case 'NONE': return '—';
      default: return '';
    }
  }

  /** Подсказка-тост почему расчёт приблизительный */
  approximateHint(product: CustomsProduct): string {
    const reasons: string[] = [];
    if (product.specificRateSkipped) reasons.push(this.reasonSpecificRateText);
    if (product.specificMinPresent) reasons.push(this.reasonMinCombinedRateText);
    if (product.exciseSkipped) reasons.push(this.reasonExciseLowerText);
    if (product.antiDumpingSkipped) reasons.push(this.reasonAntiDumpingLowerText);
    if (reasons.length === 0) return this.refineHintFallbackText;
    return this.notAccountedPrefixText + reasons.join(', ');
  }


  onBack(): void {
    this.back.emit();
  }

  // ─── Разделы кабинета ─────────────────────────────────────────────────────

  /** Перевод с запасным текстом — для подписей разделов кабинета. */
  st(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  /** Числа с разделителем тысяч по языку: «5 265 298» для ru/kk, «5,265,298» для en. */
  private numberLocale = 'ru-RU';

  fmt(value: number | null | undefined): string {
    return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 0 }).format(Number(value ?? 0));
  }

  /** Следующий раздел после текущего (без «Стоимости поставки»); null — текущий последний. */
  get nextSection(): { id: CalcSection } | null {
    const steps = CALC_SECTIONS.filter(x => x.id !== 'overview');
    const i = steps.findIndex(x => x.id === this.section);
    return i >= 0 && i < steps.length - 1 ? steps[i + 1] : null;
  }

  get nextSectionTitle(): string {
    const info = CALC_SECTIONS.find(x => x.id === this.nextSection?.id);
    return info ? this.st(info.labelKey, info.fallback) : '';
  }

  get sectionTitle(): string {
    const info = CALC_SECTIONS.find(s => s.id === this.section);
    return info ? this.st(info.labelKey, info.fallback) : '';
  }

  /** Доставка целиком (до границы + после неё; при авиа только до аэропорта), в валюте оценки. */
  get deliveryTotal(): number | null {
    const c = this.calculation;
    if (!c || (c.freightAmount == null && c.freightAfterBorderAmount == null)) return null;
    const after = c.transportType === 'AIR' ? 0 : Number(c.freightAfterBorderAmount ?? 0);
    return Number(c.freightAmount ?? 0) + after;
  }

  get deliveryCurrency(): string {
    return this.calculation?.freightCurrencyCode || 'USD';
  }

  /** Доставка в тенге по текущему курсу; null — оценки нет или курс неизвестен. */
  get deliveryKzt(): number | null {
    const total = this.deliveryTotal;
    if (total == null) return null;
    if (this.deliveryCurrency === 'KZT') return total;
    const rate = this.rates[this.deliveryCurrency];
    return rate ? total * rate : null;
  }

  /** Ориентировочная стоимость поставки: товары + таможенные платежи + доставка.
   *  Если доставляет продавец, она уже в цене товара — второй раз не прибавляем. */
  get grandTotalKzt(): number {
    const c = this.calculation;
    const delivery = this.sellerDelivers ? 0 : Number(this.deliveryKzt ?? 0);
    return Number(c?.totalProductCostKzt ?? 0) + Number(c?.totalCustomsPayments ?? 0) + delivery;
  }

  // ─── «Товары доставит продавец?» ──────────────────────────────────────────
  // Ответ хранится в расчёте на бэкенде: от него зависит таможенная стоимость. «Да» (и отсутствие ответа) —
  // доставка уже в цене товара, ничего не добавляем; «Нет» — к таможенной стоимости добавляется 80% стоимости
  // доставки (при авиа 100%). Условия поставки (Incoterms) на это больше не влияют.

  sellerSaving = false;
  /** Ответ не сохранился (сервер отказал) — показываем под вопросом, а не молчим. */
  sellerError: string | null = null;

  /** Человек уже отвечал на вопрос. */
  get sellerAnswered(): boolean {
    return this.calculation?.sellerDelivers != null;
  }

  /** Доставляет продавец. Пока ответа нет — «Да»: по умолчанию ничего не добавляем. */
  get sellerDelivers(): boolean {
    return this.calculation?.sellerDelivers !== false;
  }

  setSellerDelivers(value: boolean): void {
    if (this.readOnly) return;
    const c = this.calculation;
    if (!c || this.sellerSaving || (this.sellerAnswered && this.sellerDelivers === value)) return;
    this.sellerSaving = true;
    this.sellerError = null;
    this.customsCalculationsService.setSellerDelivers(c.id, value).subscribe({
      next: calculation => {
        this.calculation = calculation;
        this.syncEditableState();
        this.sellerSaving = false;
        this.cdr.markForCheck();
      },
      error: err => {
        this.sellerSaving = false;
        console.error('Error saving seller delivers answer:', err);
        this.sellerError = err?.status === 404
          ? this.st('calcSections.sellerSaveUnavailable', 'Не удалось сохранить ответ: на сервере пока нет этой функции. Мы уже знаем об этом — попробуйте позже.')
          : this.st('calcSections.sellerSaveError', 'Не удалось сохранить ответ. Проверьте интернет и попробуйте ещё раз.');
        this.cdr.markForCheck();
      }
    });
  }

  /** Доставку ещё нужно добавить: продавец не доставляет, а оценки пока нет. */
  get freightPending(): boolean {
    return !this.sellerDelivers && this.deliveryTotal === null;
  }

  /** Прочие расходы в итог не входят. Ориентир: фиксированное «от N ₸» или процент от стоимости товаров;
   *  где сумму заранее не назвать — «уточним по запросу» и пояснение, от чего она зависит. */
  readonly otherCosts: { key: string; fallback: string; fromKzt?: number; pctOfGoods?: [number, number]; noteKey?: string; noteFallback?: string }[] = [
    {
      key: 'calcSections.certification', fallback: 'Разрешительные документы', fromKzt: 80000,
      noteKey: 'calcSections.certificationNote', noteFallback: 'Сертификаты, декларации соответствия и другие документы — какие нужны, зависит от товара'
    },
    {
      key: 'calcSections.billoadServices', fallback: 'Услуги Bilload', fromKzt: 90000,
      noteKey: 'calcSections.billoadServicesNote', noteFallback: 'Включают услуги таможенного брокера'
    },
    {
      key: 'calcSections.warehouse', fallback: 'Услуги СВХ',
      noteKey: 'calcSections.warehouseNote', noteFallback: 'Зависит от веса и объёма груза и от того, сколько дней он пробудет на складе'
    },
    {
      key: 'calcSections.insurance', fallback: 'Страхование', pctOfGoods: [0.1, 0.2],
      noteKey: 'calcSections.insuranceNote', noteFallback: '0,1–0,2 % от стоимости груза по инвойсу'
    }
  ];

  // ─── Дорожная карта расчёта (шапка кабинета) ─────────────────────────────
  // Шаги — разделы расчёта; у каждого статус и что сейчас нужно от пользователя.

  /** Телефон: плашки дорожной карты раскрыты (по умолчанию — компактная строка). */
  progressOpen = false;

  get progressDoneCount(): number {
    return this.progressSteps.filter(s => s.state === 'done').length;
  }

  /** Ближайший незавершённый шаг — на него ведёт «Дальше» в компактной строке. */
  get progressNext(): { id: CalcSection; title: string; state: ProgressState } | null {
    return this.progressSteps.find(s => s.state !== 'done') ?? null;
  }

  get progressSteps(): { id: CalcSection; title: string; state: ProgressState; text: string }[] {
    const c = this.calculation;
    if (!c) return [];
    const steps: { id: CalcSection; state: ProgressState; key: string; fallback: string }[] = [
      { id: 'invoice', ...this.invoiceProgress },
      { id: 'freight', ...this.freightProgress },
      { id: 'payments', ...this.paymentsProgress },
      { id: 'permits', ...this.permitsProgress }
    ];
    return steps.map(s => {
      const info = CALC_SECTIONS.find(x => x.id === s.id)!;
      return { id: s.id, title: this.st(info.labelKey, info.fallback), state: s.state, text: this.st(s.key, s.fallback) };
    });
  }

  private get invoiceProgress(): StepStatus {
    const c = this.calculation!;
    if (c.invoiceProcessingStatus === 'PROCESSING') return { state: 'wait', key: 'calcSections.invoiceRecognizing', fallback: 'Распознаём инвойс…' };
    if (!c.products?.length) return { state: 'action', key: 'calcSections.invoiceEmpty', fallback: 'Добавьте товары' };
    return { state: 'done', key: 'calcSections.invoiceDone', fallback: 'Товары внесены' };
  }

  private get paymentsProgress(): StepStatus {
    const c = this.calculation!;
    if (!c.allProductsClassified) return { state: 'wait', key: 'calcSections.paymentsWait', fallback: 'Считаем платежи…' };
    if (c.hasPendingFollowUpQuestions) return { state: 'action', key: 'calcSections.paymentsQuestions', fallback: 'Ответьте на уточняющие вопросы' };
    if (this.freightPending) return { state: 'info', key: 'calcSections.paymentsPrelim', fallback: 'Уточнятся после расчёта доставки' };
    return { state: 'done', key: 'calcSections.paymentsDone', fallback: 'Посчитаны' };
  }

  private get freightProgress(): StepStatus {
    if (this.sellerAnswered && this.sellerDelivers) return { state: 'done', key: 'calcSections.freightNotNeeded', fallback: 'Не требуется — доставляет продавец' };
    if (this.deliveryTotal !== null) return { state: 'done', key: 'calcSections.freightDone', fallback: 'Рассчитан' };
    if (!this.sellerAnswered) return { state: 'action', key: 'calcSections.freightAsk', fallback: 'Ответьте, кто доставляет товар' };
    return { state: 'action', key: 'calcSections.freightEstimate', fallback: 'Укажите маршрут и рассчитайте доставку' };
  }

  /** Та же логика, что у статуса проверки документов в сводной панели. */
  private get permitsProgress(): StepStatus {
    const c = this.calculation!;
    const codes = Array.from(new Set((c.products || []).map(p => p.tnvedCodeForBroker).filter((x): x is string => !!x)));
    if (!c.allProductsClassified || codes.length === 0) return { state: 'wait', key: 'calcSections.permitsWait', fallback: 'Ждём коды ТН ВЭД…' };
    if (c.certificatesProcessingStatus === 'PROCESSING' || this.certificatesChecking) return { state: 'wait', key: 'calcSections.permitsChecking', fallback: 'Проверяем…' };
    const checklists = c.certificateChecklists || [];
    if (!codes.every(code => checklists.some(ch => ch.tnvedCode === code))) return { state: 'action', key: 'calcSections.permitsRun', fallback: 'Запустите проверку' };
    const needed = checklists.some(ch => codes.includes(ch.tnvedCode) && (ch.requiredDocuments?.length ?? 0) > 0);
    return needed
      ? { state: 'action', key: 'calcSections.permitsNeeded', fallback: 'Нужны документы — посмотрите список' }
      : { state: 'done', key: 'calcSections.permitsClear', fallback: 'Не требуются' };
  }

  get goodsValueKzt(): number {
    return Number(this.calculation?.totalProductCostKzt ?? 0);
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('public.viewCalculation.title') || this.titleText;
    this.backText = this.languageService.translate('public.viewCalculation.back') || this.backText;
    this.calculationTitleText = this.languageService.translate('public.viewCalculation.calculationTitle') || this.calculationTitleText;
    this.productsText = this.languageService.translate('public.viewCalculation.products') || this.productsText;
    this.aiIdentifiedAsText = this.languageService.translate('invoiceEditor.aiIdentifiedAs') || this.aiIdentifiedAsText;
    this.productNameText = this.languageService.translate('public.viewCalculation.productName') || this.productNameText;
    this.productCostText = this.languageService.translate('public.viewCalculation.productCost') || this.productCostText;
    this.tnvedCodeText = this.languageService.translate('public.viewCalculation.tnvedCode') || this.tnvedCodeText;
    this.customsFeesText = this.languageService.translate('public.viewCalculation.customsFees') || this.customsFeesText;
    this.customsDutyText = this.languageService.translate('public.viewCalculation.customsDuty') || this.customsDutyText;
    this.vatText = this.languageService.translate('public.viewCalculation.vat') || this.vatText;
    this.totalPaymentsText = this.languageService.translate('public.viewCalculation.totalPayments') || this.totalPaymentsText;
    this.totalAllPaymentsText = this.languageService.translate('public.viewCalculation.totalAllPayments') || this.totalAllPaymentsText;
    this.noProductsText = this.languageService.translate('public.viewCalculation.noProducts') || this.noProductsText;
    this.completedWithAiText = this.languageService.translate('public.viewCalculation.completedWithAi') || this.completedWithAiText;
    this.createdAtText = this.languageService.translate('common.createdAt') || this.createdAtText;
    this.loadingText = this.languageService.translate('common.loading') || this.loadingText;
    this.codeNotFoundText = this.languageService.translate('public.viewCalculation.codeNotFound') || this.codeNotFoundText;
    this.detailedAssessmentText = this.languageService.translate('public.viewCalculation.detailedAssessment') || this.detailedAssessmentText;
    this.whatsappContactText = this.languageService.translate('public.viewCalculation.whatsappContact') || this.whatsappContactText;

    this.calculationFallbackPrefixText = this.languageService.translate('public.viewCalculation.calculationFallbackPrefix') || this.calculationFallbackPrefixText;
    this.brokerCommentSectionTitleText = this.languageService.translate('public.viewCalculation.brokerCommentSectionTitle') || this.brokerCommentSectionTitleText;
    this.brokerCommentPlaceholderText = this.languageService.translate('public.viewCalculation.brokerCommentPlaceholder') || this.brokerCommentPlaceholderText;
    this.savingEllipsisText = this.languageService.translate('public.viewCalculation.savingEllipsis') || this.savingEllipsisText;
    this.saveBrokerCommentText = this.languageService.translate('public.viewCalculation.saveBrokerComment') || this.saveBrokerCommentText;
    this.preliminaryNoticeTitleText = this.languageService.translate('public.viewCalculation.preliminaryNoticeTitle') || this.preliminaryNoticeTitleText;
    this.preliminaryNoticeBodyText = this.languageService.translate('public.viewCalculation.preliminaryNoticeBody') || this.preliminaryNoticeBodyText;
    this.actionsText = this.languageService.translate('public.viewCalculation.actions') || this.actionsText;
    this.brokerActionsText = this.languageService.translate('public.viewCalculation.brokerActions') || this.brokerActionsText;
    this.codeInDictionaryTitleText = this.languageService.translate('public.viewCalculation.codeInDictionaryTitle') || this.codeInDictionaryTitleText;
    this.exciseNotCalculatedTitleText = this.languageService.translate('public.viewCalculation.exciseNotCalculatedTitle') || this.exciseNotCalculatedTitleText;
    this.exciseNotCalculatedBadgeText = this.languageService.translate('public.viewCalculation.exciseNotCalculatedBadge') || this.exciseNotCalculatedBadgeText;
    this.refineButtonTitleText = this.languageService.translate('public.viewCalculation.refineButtonTitle') || this.refineButtonTitleText;
    this.refineCloseLabelText = this.languageService.translate('public.viewCalculation.refineCloseLabel') || this.refineCloseLabelText;
    this.refineDoneLabelText = this.languageService.translate('public.viewCalculation.refineDoneLabel') || this.refineDoneLabelText;
    this.refineOpenLabelText = this.languageService.translate('public.viewCalculation.refineOpenLabel') || this.refineOpenLabelText;
    this.certificatesNotCheckedText = this.languageService.translate('public.viewCalculation.certificatesNotChecked') || this.certificatesNotCheckedText;
    this.certificatesCheckNotDoneText = this.languageService.translate('public.viewCalculation.certificatesCheckNotDone') || this.certificatesCheckNotDoneText;
    this.certificatesRunCheckText = this.languageService.translate('public.viewCalculation.certificatesRunCheck') || this.certificatesRunCheckText;
    this.certificatesCheckingText = this.languageService.translate('public.viewCalculation.certificatesChecking') || this.certificatesCheckingText;
    this.certificatesTitleAttrText = this.languageService.translate('public.viewCalculation.certificatesTitleAttr') || this.certificatesTitleAttrText;
    this.certificatesCloseLabelText = this.languageService.translate('public.viewCalculation.certificatesCloseLabel') || this.certificatesCloseLabelText;
    this.certificatesOpenLabelText = this.languageService.translate('public.viewCalculation.certificatesOpenLabel') || this.certificatesOpenLabelText;
    this.saveText = this.languageService.translate('common.save') || this.saveText;
    this.deleteText = this.languageService.translate('common.delete') || this.deleteText;
    this.refineSectionTitleText = this.languageService.translate('public.viewCalculation.refineSectionTitle') || this.refineSectionTitleText;
    this.refineSectionDescriptionText = this.languageService.translate('public.viewCalculation.refineSectionDescription') || this.refineSectionDescriptionText;
    this.refineNoQuestionsText = this.languageService.translate('public.viewCalculation.refineNoQuestions') || this.refineNoQuestionsText;
    this.enterPrefixText = this.languageService.translate('public.viewCalculation.enterPrefix') || this.enterPrefixText;
    this.defaultUnitText = this.languageService.translate('public.viewCalculation.defaultUnit') || this.defaultUnitText;
    this.refinePreviewButtonTitleText = this.languageService.translate('public.viewCalculation.refinePreviewButtonTitle') || this.refinePreviewButtonTitleText;
    this.calculatingText = this.languageService.translate('public.viewCalculation.calculating') || this.calculatingText;
    this.calculateButtonText = this.languageService.translate('public.viewCalculation.calculateButton') || this.calculateButtonText;
    this.refineSaveButtonTitleText = this.languageService.translate('public.viewCalculation.refineSaveButtonTitle') || this.refineSaveButtonTitleText;
    this.saveButtonWithIconText = this.languageService.translate('public.viewCalculation.saveButtonWithIcon') || this.saveButtonWithIconText;
    this.cancelText = this.languageService.translate('common.cancel') || this.cancelText;
    this.savedText = this.languageService.translate('public.viewCalculation.saved') || this.savedText;
    this.previewResultUnsavedText = this.languageService.translate('public.viewCalculation.previewResultUnsaved') || this.previewResultUnsavedText;
    this.countryOfOriginText = this.languageService.translate('public.viewCalculation.countryOfOrigin') || this.countryOfOriginText;
    this.dutyRateText = this.languageService.translate('public.viewCalculation.dutyRate') || this.dutyRateText;
    this.exciseText = this.languageService.translate('public.viewCalculation.excise') || this.exciseText;
    this.antiDumpingDutyText = this.languageService.translate('public.viewCalculation.antiDumpingDuty') || this.antiDumpingDutyText;
    this.totalCustomsPaymentsFullText = this.languageService.translate('public.viewCalculation.totalCustomsPaymentsFull') || this.totalCustomsPaymentsFullText;
    this.tnvedColonLabelText = this.languageService.translate('public.viewCalculation.tnvedColonLabel') || this.tnvedColonLabelText;
    this.cachedResultSuffixText = this.languageService.translate('public.viewCalculation.cachedResultSuffix') || this.cachedResultSuffixText;
    this.noNonTariffDataText = this.languageService.translate('public.viewCalculation.noNonTariffData') || this.noNonTariffDataText;
    this.requiredDocumentsTitleText = this.languageService.translate('public.viewCalculation.requiredDocumentsTitle') || this.requiredDocumentsTitleText;
    this.noDocumentsRequiredText = this.languageService.translate('public.viewCalculation.noDocumentsRequired') || this.noDocumentsRequiredText;
    this.noteLabelText = this.languageService.translate('public.viewCalculation.noteLabel') || this.noteLabelText;
    this.aiDisclaimerText = this.languageService.translate('public.viewCalculation.aiDisclaimer') || this.aiDisclaimerText;
    this.reasonSpecificRateText = this.languageService.translate('public.viewCalculation.reasonSpecificRate') || this.reasonSpecificRateText;
    this.reasonMinCombinedRateText = this.languageService.translate('public.viewCalculation.reasonMinCombinedRate') || this.reasonMinCombinedRateText;
    this.reasonExciseLowerText = this.languageService.translate('public.viewCalculation.reasonExciseLower') || this.reasonExciseLowerText;
    this.reasonAntiDumpingLowerText = this.languageService.translate('public.viewCalculation.reasonAntiDumpingLower') || this.reasonAntiDumpingLowerText;
    this.refineHintFallbackText = this.languageService.translate('public.viewCalculation.refineHintFallback') || this.refineHintFallbackText;
    this.notAccountedPrefixText = this.languageService.translate('public.viewCalculation.notAccountedPrefix') || this.notAccountedPrefixText;
  }

  private syncEditableState(): void {
    this.brokerCommentDraft = this.calculation?.brokerComment || '';
    this.editableProducts = {};
    this.calculation?.products?.forEach(product => {
      this.editableProducts[product.id] = this.createEditableProduct(product);
    });
  }

  private createEditableProduct(product: CustomsProduct): UpdateCustomsProductBrokerRequest {
    return {
      nameForBroker: product.nameForBroker || product.nameOriginal || '',
      productCost: product.productCostKzt ?? product.productCost ?? 0,
      tnvedCodeForBroker: product.tnvedCodeForBroker || product.tnvedCodeOriginal || '',
      customsFees: product.customsFees ?? 0,
      customsDuty: product.customsDuty ?? 0,
      vat: product.vat ?? 0,
      totalCustomsPayments: product.totalCustomsPayments ?? 0
    };
  }

  onInvoiceUpdated(updated: CustomsCalculation): void {
    this.calculation = updated;
    this.syncEditableState();
    this.cdr.markForCheck();
  }

  /** Кнопка «Рассчитать стоимость доставки» сама сохраняет черновик формы поставки
   *  (маршрут/вес/объём/тип перевозки), а затем сразу запускает AI-оценку — пользователю
   *  не нужно отдельно нажимать «Сохранить» перед расчётом. */
  onEstimateDeliveryCost(shipmentInfo: UpdateShipmentInfoRequest): void {
    if (this.readOnly) return;
    if (!this.calculation || this.estimatingDelivery) return;
    this.estimatingDelivery = true;
    this.deliveryEstimateError = null;
    const calculationId = this.calculation.id;
    this.customsCalculationsService.updateShipmentInfo(calculationId, shipmentInfo).pipe(
      switchMap(() => this.customsCalculationsService.estimateDeliveryCost(calculationId))
    ).subscribe({
      next: (calculation) => {
        this.calculation = calculation;
        this.syncEditableState();
        this.estimatingDelivery = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.estimatingDelivery = false;
        this.deliveryEstimateError = err.error?.message || 'Не удалось рассчитать стоимость доставки';
        this.cdr.markForCheck();
      }
    });
  }

  /** Изменение Incoterms из блока товаров/инвойса — сохраняем полным набором данных о
   *  поставке (бэкенд перезаписывает все поля сразу), беря остальные значения из уже
   *  загруженного расчёта, чтобы не затереть их этим точечным изменением. */
  onInvoiceIncotermChange(incotermCode: string): void {
    if (this.readOnly) return;
    if (!this.calculation || this.isBroker()) return;
    const c = this.calculation;
    this.incotermSaving = true;
    this.customsCalculationsService.updateShipmentInfo(c.id, {
      fromCountryId: c.fromCountryId,
      fromCity: c.fromCity,
      toCountryId: c.toCountryId,
      toCity: c.toCity,
      incotermCode: incotermCode || undefined,
      totalWeightKg: c.totalWeightKg,
      totalVolumeM3: c.totalVolumeM3,
      transportType: c.transportType,
      freightEnabled: c.freightEnabled,
      freightAmount: c.freightAmount,
      freightAfterBorderAmount: c.freightAfterBorderAmount,
      freightCurrencyCode: c.freightCurrencyCode
    }).subscribe({
      next: (calculation) => {
        this.calculation = calculation;
        this.syncEditableState();
        this.incotermSaving = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.incotermSaving = false;
        console.error('Error saving incoterm:', err);
        this.cdr.markForCheck();
      }
    });
  }
}
